    /* eslint-disable react/prop-types */
import { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import Papa from "papaparse";
import { format } from "date-fns";

// ── helpers ───────────────────────────────────────────────────────────────────

const REFUND_STATUS_STYLES = {
  SUCCESS: { bg: "#d4edda", color: "#155724" },
  PENDING: { bg: "#fff3cd", color: "#856404" },
  FAILED:  { bg: "#f8d7da", color: "#721c24" },
};

function StatusBadge({ value }) {
  if (!value) return <span>—</span>;
  const s = REFUND_STATUS_STYLES[value] ?? { bg: "#e2e3e5", color: "#383d41" };
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: s.bg,
        color: s.color,
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </MDBox>
  );
}

function CopyCell({ value }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  if (!value) return <span>—</span>;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: "4px", cursor: "default" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={value}
    >
      <span style={{ maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" }}>
        {`${value.slice(0, 8)}…`}
      </span>
      <span
        onClick={handleCopy}
        style={{ cursor: "pointer", color: copied ? "#1fa971" : "#989daa", fontSize: "1rem", visibility: hovered ? "visible" : "hidden", flexShrink: 0 }}
      >
        {copied ? "✓" : "⎘"}
      </span>
    </span>
  );
}

const columns = [
  {
    Header: "Refund ID",
    accessor: "refundId",
    width: "10%",
    align: "left",
    Cell: ({ value }) => <CopyCell value={value} />,
  },
  {
    Header: "Payment ID",
    accessor: "paymentId",
    width: "10%",
    align: "left",
    Cell: ({ value }) => <CopyCell value={value} />,
  },
  {
    Header: "Merchant Txn ID",
    accessor: "merchantTransactionId",
    width: "12%",
    align: "left",
    Cell: ({ value }) => <CopyCell value={value} />,
  },
  {
    Header: "Processor",
    accessor: "processorType",
    width: "8%",
    align: "left",
  },
  {
    Header: "Processor Ref",
    accessor: "processorRefundId",
    width: "10%",
    align: "left",
    Cell: ({ value }) => (value ? <CopyCell value={value} /> : <span>—</span>),
  },
  {
    Header: "Amount",
    accessor: (row) =>
      row.amountInCents != null
        ? `${(parseFloat(row.amountInCents) / 100).toFixed(2)} ${row.currency ?? ""}`.trim()
        : "—",
    width: "9%",
    align: "left",
  },
  {
    Header: "Currency",
    accessor: "currency",
    width: "7%",
    align: "left",
  },
  {
    Header: "Status",
    accessor: "status",
    width: "8%",
    align: "left",
    Cell: ({ value }) => <StatusBadge value={value} />,
  },
  {
    Header: "Reason",
    accessor: "reason",
    width: "15%",
    align: "left",
    Cell: ({ value }) => (
      <span title={value} style={{ display: "inline-block", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value ?? "—"}
      </span>
    ),
  },
  {
    Header: "Created Date",
    accessor: "createdDate",
    width: "11%",
    align: "left",
    Cell: ({ value }) => {
      if (!value) return <span>—</span>;
      const d = new Date(value);
      return <span>{isNaN(d) ? value : d.toLocaleString()}</span>;
    },
  },
];

// ── main page ─────────────────────────────────────────────────────────────────

function RefundReport({ embedded = false, tabsSlot = null } = {}) {
  const { predefinedRanges } = reportsLineChartData;
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [filterSearch, setFilterSearch] = useState("");

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [dateRange, setDateRange] = useState([today, today]);

  const api = useApi();

  const filteredRows = useMemo(() => {
    const search = filterSearch.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "All" && r.status !== statusFilter) return false;
      if (search) {
        const searchableValues = [
          r.refundId,
          r.paymentId,
          r.merchantTransactionId,
          r.processorRefundId,
          r.amountInCents,
          r.amountInCents != null ? (parseFloat(r.amountInCents) / 100).toFixed(2) : null,
          r.reason,
        ];
        if (!searchableValues.some((v) => v != null && String(v).toLowerCase().includes(search))) return false;
      }
      return true;
    });
  }, [rows, statusFilter, filterSearch]);

  const fetchData = (range) => {
    if (!range || range.length !== 2) return;
    api
      .post("refund-report", {
        startDate: format(range[0], "MM/dd/yyyy") + " 00:00:00",
        endDate: format(range[1], "MM/dd/yyyy") + " 23:59:59",
        paymentId: null,
        status: null,
        processorType: null,
      })
      .then((res) => {
        setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setRows([]);
      });
  };

  const handleDownloadCSV = () => {
    if (filteredRows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const headers = columns.map((c) => c.Header);
    const values = filteredRows.map((row) =>
      columns.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    const csv = Papa.unparse([headers, ...values], { quotes: true });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `RefundReport_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    fetchData(dateRange);
  }, []); // eslint-disable-line

  const selectSx = {
    minWidth: 160,
    "& .MuiOutlinedInput-root": { height: "36.5px" },
    "& .MuiInputLabel-root": { top: "-4px" },
    "& .MuiInputLabel-shrink": { top: 0 },
  };

  const content = (
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid item xs={4}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    Reports
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="regular" color="secondary">
                    {filteredRows.length} Refunds
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="center">
                <DateRangePicker
                  shouldDisableDate={afterToday()}
                  ranges={predefinedRanges}
                  value={dateRange}
                  onChange={(range) => {
                    if (range && range.length === 2) {
                      setDateRange(range);
                      fetchData(range);
                    }
                  }}
                  format="d MMMM yyyy"
                />
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="flex-end">
                <MDButton variant="gradient" color="info" onClick={handleDownloadCSV}>
                  Export CSV
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
          {tabsSlot && (
            <Grid item xs={12}>
              {tabsSlot}
            </Grid>
          )}
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
            <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <TextField
                  label="Search"
                  placeholder="Refund ID, Payment ID, Merchant Txn Id, Processor Ref, Amount, Reason..."
                  size="small"
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { height: "36.5px" } }}
                />

                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="SUCCESS">Success</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                  </Select>
                </FormControl>
              </MDBox>

              {rows.length > 0 && (
                <MDBox display="flex" gap={2} mt={1.5} flexWrap="wrap">
                  <MDTypography variant="caption" color="text">
                    Total: <strong>{filteredRows.length}</strong> refunds
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Success:{" "}
                    <strong style={{ color: "#155724" }}>
                      {filteredRows.filter((r) => r.status === "SUCCESS").length}
                    </strong>
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Pending:{" "}
                    <strong style={{ color: "#856404" }}>
                      {filteredRows.filter((r) => r.status === "PENDING").length}
                    </strong>
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Failed:{" "}
                    <strong style={{ color: "#721c24" }}>
                      {filteredRows.filter((r) => r.status === "FAILED").length}
                    </strong>
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            <DataTable
              table={{ columns, rows: filteredRows }}
              showTotalEntries
              isSorted
              noEndBorder
              hideExpandColumn
              entriesPerPage
              entriesPerPagePosition="bottom"
              canSearch={false}
            />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
      <Footer />
    </DashboardLayout>
  );
}

export default RefundReport;